import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import {
  ContractFormDto,
  type ContractFormInterface
} from '@zaoblako/shared/modules/document/dtos/contract/contract-form.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ContractService } from '@zaoblako/web/core/services/finance/contract.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { DocumentFileTemplateSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-file-template-single-select/document-file-template-single-select.component';

@Component({
  selector: 'crm-contract-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    DatepickerComponent,
    JoinPipe,
    ButtonComponent,
    SpinnerComponent,
    ContragentAutoCompleteComponent,
    CheckboxModule,
    FormsModule,
    DirectionSingleSelectComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    CostCenterSingleSelectComponent,
    DealAutoCompleteComponent,
    CompanySingleSelectComponent,
    DocumentFileTemplateSingleSelectComponent
  ],
  templateUrl: './contract-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractFormComponent extends BaseFormComponent<ContractFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public contract: ContractFormInterface = {
    number: null,
    name: null,
    company: null,
    contragent: null,
    date: moment().toDate()
  };

  public dto = ContractFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public clientId: number;

  public constructor(
    private readonly contractService: ContractService,
    private readonly directionService: DirectionService,
    private readonly formStoreService: FormStoreService,
    private readonly contragentService: ContragentService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    public readonly accessService: AccessService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.clientId = this.formStoreService.getState.forms[this.index]?.data?.clientId;
    const contragentId = this.formStoreService.getState.forms[this.index]?.data?.contragentId;
    const copyId = this.formStoreService.getState.forms[this.index]?.data?.copyId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([this.directionService.find<DirectionFindInterface>()]).subscribe({
      next: ([directions]) => {
        this.directions = directions;
        this.cdRef.markForCheck();
      }
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }

    if (!isNaN(contragentId)) {
      this.contragentService.findById<ContragentFindInterface>(contragentId).subscribe((contragent) => {
        this.contract.contragent = contragent;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(copyId)) {
      this.contractService.findById<ContractFindInterface>(copyId).subscribe((contract) => {
        this.contract = contract;
        this.cdRef.markForCheck();
      });
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.contractService
      .findById<ContractFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (contract) => {
          this.contract = contract;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveContract() {
    if (this.checkValidate(this.contract).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.contractService.update<ContractFormInterface, ContractFindInterface>(this.contract).subscribe({
          next: (contract) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Договор "${this.contract.name}" обновлен!`);
            this.formStoreService.saveNext(contract, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.contractService.create<ContractFindInterface>(this.contract).subscribe({
          next: (contract) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Договор создан!');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/client/contract/card/' + contract.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(contract, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteContract() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить договор "${this.contract?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.contractService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Договор "${this.contract?.name}" удален!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
